/**
 * ONE OVERLAY OPENED FROM INSIDE ANOTHER (wave 1c, `file-attachments.md` finding 2).
 *
 * THE PROBLEM, AND IT IS NOT ABOUT FILES. A `Dialog` portals to the body, so it is not a DOM
 * descendant of whatever opened it. A MODAL layer does not care - Radix's dialog refuses to
 * dismiss on focus leaving and disables outside pointer events, which is why a Select inside
 * a Dialog has always worked here. A NON-MODAL one does: `Popover` and `CellEditor` dismiss
 * on a pointer outside and on focus leaving, and a confirmation opened from inside one is
 * "outside" it by every test they have. The cell editor's panel therefore closed - and
 * unmounted the confirmation with it - the instant the confirmation opened. Measured in wave
 * 1b: zero dialogs on the page 1.5s after the press.
 *
 * THE MARK IS ON THE DOM, and the reason is that two different mechanisms have to ask the
 * same question. Radix asks it inside `DismissableLayer` (it ships a `Branch` for exactly
 * this, registered in an effect); a caller asks it in its own handlers - `RecordTableCell`
 * closes its editor from an `onBlur` on the panel, and React propagates events from a portal
 * through the REACT tree, so that blur fires for focus moving into the portalled dialog too.
 * An attribute answers both, and a second mechanism for one question is how two panels in one
 * product start behaving differently.
 *
 * IT IS OPT-IN, and deliberately. A dialog opened from a DROPDOWN MENU should close that
 * menu; a confirmation opened from a cell editor must not close the editor. Only the caller
 * knows which of the two it is building.
 */
export const NESTED_LAYER_ATTRIBUTE = 'data-nested-layer';

/**
 * Whether an event's target sits inside an overlay that declared itself nested. Reads the
 * DOM at event time rather than a registry, so no mount order can make it wrong.
 */
export function isWithinNestedLayer(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false;
  const element = target instanceof Element ? target : target.parentElement;
  return element !== null && element.closest(`[${NESTED_LAYER_ATTRIBUTE}]`) !== null;
}

/** Spread onto an overlay's portalled elements. Empty when the overlay is not nested. */
export function nestedLayerAttributes(nested: boolean): Record<string, string> {
  return nested ? { [NESTED_LAYER_ATTRIBUTE]: '' } : {};
}
